import { TFunction } from 'next-i18next';
import { LOCALE_COMMON_PATHS, SiteLanguage } from 'types/locales';
import { i18n } from 'next-i18next';

/**
 * Maps province codes to their French abbreviations based on official Canadian standards
 */
const FRENCH_PROVINCE_ABBREVIATIONS: Record<string, string> = {
  AB: 'Alb.',
  BC: 'C.-B.',
  MB: 'Man.',
  NB: 'N.-B.',
  NL: 'T.-N.-L.',
  NT: 'T.N.-O.',
  NS: 'N.-É.',
  NU: 'Nt',
  ON: 'Ont.',
  PE: 'Î.-P.-É.',
  QC: 'Qc',
  SK: 'Sask.',
  YT: 'Yn',
};

/**
 * Maps province codes to their English abbreviations
 */
const ENGLISH_PROVINCE_ABBREVIATIONS: Record<string, string> = {
  AB: 'AB',
  BC: 'BC',
  MB: 'MB',
  NB: 'NB',
  NL: 'NL',
  NT: 'NT',
  NS: 'NS',
  NU: 'NU',
  ON: 'ON',
  PE: 'PE',
  QC: 'QC',
  SK: 'SK',
  YT: 'YT',
};

/**
 * Normalizes a province name to snake_case for use as a translation key
 * e.g., "British Columbia" → "british_columbia"
 * e.g., "Newfoundland and Labrador" → "newfoundland_and_labrador"
 */
function normalizeProvinceName(provinceName: string): string {
  return provinceName
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[()]/g, ''); // Remove parentheses if any
}

/**
 * Extracts the province code from a string like "Ontario (ON)"
 * Returns the code (e.g., "ON") or null if no code is found
 */
function extractProvinceCode(provinceNameWithCode: string): string | null {
  const match = provinceNameWithCode.match(/\(([A-Z]{2})\)/);
  return match ? match[1] : null;
}

/**
 * Extracts the province name without the code from a string like "Ontario (ON)"
 * Returns just the name part (e.g., "Ontario")
 */
function extractProvinceName(provinceNameWithCode: string): string {
  return provinceNameWithCode.replace(/\s*\([A-Z]{2}\)\s*$/, '').trim();
}

/**
 * Gets the appropriate abbreviation for a province code based on the current locale
 * @param code - The province code (e.g., "ON", "QC")
 * @param locale - The current locale (e.g., "en", "fr", "fr-CA")
 * @returns The localized abbreviation
 */
function getLocalizedAbbreviation(code: string, locale: string): string {
  const isFrenchCanadian = locale === SiteLanguage.FrenchCA;
  
  if (isFrenchCanadian && FRENCH_PROVINCE_ABBREVIATIONS[code]) {
    return FRENCH_PROVINCE_ABBREVIATIONS[code];
  }
  
  return ENGLISH_PROVINCE_ABBREVIATIONS[code] || code;
}

/**
 * Maps normalized province names to their LOCALE_COMMON_PATHS constants
 */
const PROVINCE_TRANSLATION_KEYS: Record<string, keyof typeof LOCALE_COMMON_PATHS> = {
  alberta: 'PROVINCES_ALBERTA',
  british_columbia: 'PROVINCES_BRITISH_COLUMBIA',
  manitoba: 'PROVINCES_MANITOBA',
  new_brunswick: 'PROVINCES_NEW_BRUNSWICK',
  newfoundland_and_labrador: 'PROVINCES_NEWFOUNDLAND_AND_LABRADOR',
  northwest_territories: 'PROVINCES_NORTHWEST_TERRITORIES',
  nova_scotia: 'PROVINCES_NOVA_SCOTIA',
  nunavut: 'PROVINCES_NUNAVUT',
  ontario: 'PROVINCES_ONTARIO',
  prince_edward_island: 'PROVINCES_PRINCE_EDWARD_ISLAND',
  quebec: 'PROVINCES_QUEBEC',
  saskatchewan: 'PROVINCES_SASKATCHEWAN',
  yukon: 'PROVINCES_YUKON',
};

/**
 * Translates a province name using i18next translation system
 * Handles province names with or without codes (e.g., "Ontario (ON)" or "Ontario")
 * Translates the name and uses appropriate abbreviations based on locale
 * 
 * @param provinceNameFromApi - The province name from the API (e.g., "Ontario (ON)")
 * @param t - The translation function from useTranslation hook
 * @returns The translated province name with localized abbreviation (e.g., "Ontario (Ont.)" in French)
 * 
 * @example
 * // English locale
 * translateProvinceName("Ontario (ON)", t) // Returns: "Ontario (ON)"
 * 
 * @example
 * // French locale
 * translateProvinceName("British Columbia (BC)", t) // Returns: "Colombie-Britannique (C.-B.)"
 */
export function translateProvinceName(provinceNameFromApi: string, t: TFunction): string {
  // Extract the province code if present
  const provinceCode = extractProvinceCode(provinceNameFromApi);
  
  // Extract the province name without the code
  const provinceName = extractProvinceName(provinceNameFromApi);
  
  // Normalize the province name to snake_case for the translation key
  const normalizedName = normalizeProvinceName(provinceName);
  
  // Get the translation key constant
  const translationKeyConstant = PROVINCE_TRANSLATION_KEYS[normalizedName];
  
  // Get the translated province name
  const translatedName = translationKeyConstant 
    ? t(LOCALE_COMMON_PATHS[translationKeyConstant], {
      defaultValue: provinceName,
    })
    : provinceName; // Fallback to original name if no translation key found
  
  // If there's a province code, append the localized abbreviation
  if (provinceCode) {
    // Get current locale - check both i18n.language and i18n.resolvedLanguage
    const locale = i18n.resolvedLanguage || i18n.language || 'en';
    const localizedAbbreviation = getLocalizedAbbreviation(provinceCode, locale);
    return `${translatedName} (${localizedAbbreviation})`;
  }
  
  // Return just the translated name if no code was present
  return translatedName;
}

